import * as R from 'ramda';
import { Functional, MapNote } from '@wayfinder/vms-base';
import { CanonicalLoader } from '@wayfinder/vms-state';
import def from './define-action';
import * as Telemetry from '../telemetry.js';

const INITIAL_STATE = {
    totals: { open: 0, resolved: 0 },
    mapNotelets: [],
    filter: {
        status: 'open',
        type: {
            noType: true,
            mapRequest: true,
            fieldVerify: true,
        },
    },
    showNotesOnMap: true,
    filterLabel: '',
    filterAmount: 0,
    selectedMapNote: null,
    updatedFields: {},
    comment: '',
    showExitMapNoteModal: false,
    showTrashMapNoteModal: false,
    sheetVersion: '',
    showCancelChangesModal: false,
    loading: false,
    dropdownShowing: false,
    filterExpanded: false,
};

// =====================================================================================================================
// Action Constants
// =====================================================================================================================

const ActionConstants = {
    SET_MAP_NOTELETS: 'SET_MAP_NOTELETS',
    SET_MAP_NOTE_FILTER: 'SET_MAP_NOTE_FILTER',
    SET_TOTALS: 'SET_TOTALS',
    SET_SHOW_NOTES_ON_MAP: 'SET_SHOW_NOTES_ON_MAP',
    TOGGLE_NOTES_ON_MAP: 'TOGGLE_NOTES_ON_MAP',
    SET_SELECTED_MAP_NOTE: 'SET_SELECTED_MAPNOTE',
    SET_UPDATED_FIELDS: 'SET_UPDATED_FIELDS',
    SET_SHOW_TYPE_DROPDOWN: 'SET_SHOW_TYPE_DROPDOWN',
    SET_SHOW_TRASH_MAP_NOTE_MODAL: 'SET_SHOW_TRASH_MAP_NOTE_MODAL',
    SET_SHOW_EXIT_MAP_NOTE_MODAL: 'SET_SHOW_EXIT_MAP_NOTE_MODAL',
    SET_SHOW_CANCEL_CHANGES_MODAL: 'SET_SHOW_CANCEL_CHANGES_MODAL',
    CLEAR_UPDATED_FIELDS: 'CLEAR_UPDATED_FIELDS',
    SET_COMMENT: 'SET_COMMENT',
    SET_SHEET_VERSION: 'SET_SHEET_VERSION',
    SET_LOADING: 'SET_LOADING',
    SET_DROPDOWN_SHOWING: 'SET_DROPDOWN_SHOWING',
    SET_FILTER_EXPANDED: 'SET_FILTER_EXPANDED',
};

// =====================================================================================================================
// Actions
// =====================================================================================================================

const Actions = {
    setMapNoteFilter: def(ActionConstants.SET_MAP_NOTE_FILTER, 'filter'),
    setTotals: def(ActionConstants.SET_TOTALS, 'totals'),
    setMapNotelets: def(ActionConstants.SET_MAP_NOTELETS, 'mapNotelets'),
    setShowNotesOnMap: def(ActionConstants.SET_SHOW_NOTES_ON_MAP, 'showNotesOnMap'),
    toggleNotesOnMap: def(ActionConstants.TOGGLE_NOTES_ON_MAP, 'showNotesOnMap'),
    setSelectedMapNote: def(ActionConstants.SET_SELECTED_MAP_NOTE, 'selectedMapNote'),
    setUpdatedFields: def(ActionConstants.SET_UPDATED_FIELDS, 'updatedFields'),
    setShowTypeDropdown: def(ActionConstants.SET_SHOW_TYPE_DROPDOWN, 'showTypeDropdown'),
    setShowTrashMapNoteModal: def(ActionConstants.SET_SHOW_TRASH_MAP_NOTE_MODAL, 'showTrashMapNoteModal'),
    setShowExitMapNoteModal: def(ActionConstants.SET_SHOW_EXIT_MAP_NOTE_MODAL, 'showExitMapNoteModal'),
    setShowCancelChangesModal: def(ActionConstants.SET_SHOW_CANCEL_CHANGES_MODAL, 'showCancelChangesModal'),
    clearUpdatedFields: def(ActionConstants.CLEAR_UPDATED_FIELDS, 'updatedFields'),
    setComment: def(ActionConstants.SET_COMMENT, 'comment'),
    setSheetVersion: def(ActionConstants.SET_SHEET_VERSION, 'sheetVersion'),
    setLoading: def(ActionConstants.SET_LOADING, 'loading'),
    setDropdownShowing: def(ActionConstants.SET_DROPDOWN_SHOWING, 'dropdownShowing', 'venueId'),
    setFilterExpanded: def(ActionConstants.SET_FILTER_EXPANDED, 'filterExpanded'),
};

// ---------------------------------------------------------------------------------------------------------------------
//
// HANDLERS
//
// ---------------------------------------------------------------------------------------------------------------------
const handleSetTotals = (state, action) => R.assoc('totals', action.totals, state);
const handleSetMapNotelets = (state, action) => R.assoc('mapNotelets', action.mapNotelets, state);
const handleSetMapNoteFilter = (state, action) => R.assoc('filter', action.filter, state);
const handleSetShowNotesOnMap = (state, action) => R.assoc('showNotesOnMap', action.showNotesOnMap, state);
const handleToggleNotesOnMap = state => R.assoc('showNotesOnMap', !state.showNotesOnMap, state);
const handleSetSelectedMapNote = (state, action) => R.assoc('selectedMapNote', action.selectedMapNote, state);
const handleSetUpdatedFields = (state, action) =>
    R.assoc('updatedFields', R.mergeRight(state.updatedFields, action.updatedFields), state);
const handleSetShowTypeDropdown = (state, action) => R.assoc('showTypeDropdown', action.showTypeDropdown, state);
const handleSetShowTrashMapNoteModal = (state, action) =>
    R.assoc('showTrashMapNoteModal', action.showTrashMapNoteModal, state);
const handleSetShowExitMapNoteModal = (state, action) =>
    R.assoc('showExitMapNoteModal', action.showExitMapNoteModal, state);
const handleSetShowCancelChangesModal = (state, action) =>
    R.assoc('showCancelChangesModal', action.showCancelChangesModal, state);
const handleClearUpdatedFields = state => R.assoc('updatedFields', {}, state);
const handleSetComment = (state, action) => R.assoc('comment', action.comment, state);
const handleSetSheetVersion = (state, action) => R.assoc('sheetVersion', action.sheetVersion, state);
const handleSetLoading = (state, action) => R.assoc('loading', action.loading, state);
const handleSetFilterExpanded = (state, action) => R.assoc('filterExpanded', action.filterExpanded, state);

const handleSetDropdownShowing = (state, action) => {
    if (action.venueId) Telemetry.recordAddDropdown(action.venueId);

    return R.assoc('dropdownShowing', action.dropdownShowing, state);
};

// ---------------------------------------------------------------------------------------------------------------------
//
// SELECTORS
//
// ---------------------------------------------------------------------------------------------------------------------
const findMapNotesOnLevels = masterState => calculateMapNotesOnLevels(masterState.mapNotes);
const getSelectedMapNote = masterState => masterState.mapNotes.selectedMapNote;
const getSheetVersion = masterState => masterState.mapNotes.sheetVersion;
const getLoading = masterState => masterState.mapNotes.loading;
const getTotals = masterState => masterState.mapNotes.totals;
const getMapNotelets = masterState => masterState.mapNotes.mapNotelets;
const getMapNotesFilter = masterState => masterState.mapNotes.filter;
const getMapNotesFilterAmount = masterState => calculateMapNotesFilterAmount(masterState.mapNotes);
const getMapNotesFilterLabel = masterState => Functional.capitalizeFirst(masterState.mapNotes.filter.status);
const getShowExitMapNoteModal = masterState => masterState.mapNotes.showExitMapNoteModal;
const getShowNotesOnMap = masterState => masterState.mapNotes.showNotesOnMap;
const getShowTrashMapNoteModal = masterState => masterState.mapNotes.showTrashMapNoteModal;
const getShowCancelChangesModal = masterState => masterState.mapNotes.showCancelChangesModal;
const getComment = masterState => masterState.mapNotes.comment;
const getUpdatedFields = masterState => masterState.mapNotes.updatedFields;
const isTypeDropdownOpen = masterState => masterState.mapNotes.showTypeDropdown;
const getDropdownShowing = masterState => masterState.mapNotes.dropdownShowing;
const getFilterExpanded = masterState => masterState.mapNotes.filterExpanded;
const getConvertedMapNoteletsAsGeoJson = (masterState, ordinal) => calculateGeoJson(masterState.mapNotes, ordinal);
const getId = masterState => masterState.mapNotes.selectedMapNote.id;
const getStatus = masterState => masterState.mapNotes.selectedMapNote.noteStatus;
const getMapNotelet = R.curry((masterState, id) => R.find(R.propEq(id, 'id'), getMapNotelets(masterState)));

// ---------------------------------------------------------------------------------------------------------------------
//
// THUNKS
//
// ---------------------------------------------------------------------------------------------------------------------

/*
 * Helper Function
 * Print the exception to the console
 * @sig handleException :: (String)
 */
const handleException = e => console.log(e);

/*
 * Helper Function
 * Update the map notelets based on changes
 * @sig changeMapNoteTally :: (String, Object, DispatchFunction)
 */
const changeMapNoteTally = (status, totals) => async dispatch => {
    if (status === 'open') {
        dispatch(
            Actions.setTotals({
                open: totals.open + 1,
                resolved: totals.resolved - 1,
            }),
        );
    } else {
        dispatch(
            Actions.setTotals({
                open: totals.open - 1,
                resolved: totals.resolved + 1,
            }),
        );
    }
};

/*
 * Helper Function
 * Update the map notelets based on changes
 * @sig updateMapNotelets :: (Number, [Object], Object, DispatchFunction)
 */
const updateMapNotelets = (noteId, mapNotelets, updatedMapNote) => async dispatch => {
    const updatedMapNotelet = MapNote.MapNote.removeFields(
        ['comments', 'createdBy', 'dueAt', 'isPublic'],
        updatedMapNote,
    );
    const matchKeyValue = R.propEq(noteId, 'id');
    const findIndex = R.findIndex(matchKeyValue, mapNotelets);
    dispatch(Actions.setMapNotelets(R.update(findIndex, updatedMapNotelet, mapNotelets)));
};

/*
 * Return Map Note information from the API
 * @sig findSelectedMapNote :: (String, Number)
 */
const findSelectedMapNote = async (venueId, noteId) => {
    try {
        const selectedMapNote = await CanonicalLoader.getSelectedMapNote(venueId, noteId);
        return selectedMapNote;
    } catch (e) {
        console.log(e);
    }
};

/*
 * Add a text comment to the map note
 * @sig addMapNoteCommentText :: () -> async (DispatchFunction, State)
 */
const addMapNoteCommentText = (venueId, email) => async (dispatch, getState) => {
    const noteId = getId(getState());
    const comment = { comment: getState().mapNotes.comment };
    dispatch(Actions.setLoading(true));
    try {
        const updatedMapNote = await CanonicalLoader.addMapNoteComment(venueId, email, noteId, comment);
        dispatch(Actions.setSelectedMapNote(updatedMapNote));
        dispatch(Actions.setComment(''));
    } catch (e) {
        handleException(e);
    }
    dispatch(Actions.setLoading(false));
};

/*
 * Add one or more image comments to the map note
 * @sig addMapNoteCommentImages :: [Object] -> async (DispatchFunction, State)
 */
const addMapNoteCommentImages = (images, venueId, email) => async (dispatch, getState) => {
    const noteId = getId(getState());
    const addImageComment = async (image, index) => {
        try {
            const updatedMapNote = await CanonicalLoader.addMapNoteComment(venueId, email, noteId, image);
            // Don't dispatch until getting response back from last comment so only need to re-render again once
            if (R.equals(R.length(images) - 1, index)) {
                dispatch(Actions.setSelectedMapNote(updatedMapNote));
            }
        } catch (e) {
            handleException(e);
        }
    };
    dispatch(Actions.setLoading(true));
    const mapWithIndex = R.addIndex(R.map);
    // For every comment, make an API call to add a map note comment
    await Promise.all(mapWithIndex(addImageComment, images));
    dispatch(Actions.setLoading(false));
};

/*
 * Delete a comment the map note
 * @sig deleteMapNoteComment :: Number -> async (DispatchFunction, State)
 */
const deleteMapNoteComment = (commentId, venueId, email) => async (dispatch, getState) => {
    const mapNote = getSelectedMapNote(getState());
    dispatch(Actions.setLoading(true));
    const deletedComment = MapNote.MapNoteComment.findComment(commentId, mapNote.comments);
    const updatedComments = MapNote.MapNoteComment.updateComment(deletedComment, mapNote.comments);
    const updatedMapNote = R.assoc('comments', updatedComments, mapNote);
    try {
        await CanonicalLoader.deleteMapNoteComment(venueId, email, mapNote.id, commentId);
        dispatch(Actions.setSelectedMapNote(updatedMapNote));
    } catch (e) {
        handleException(e);
    }
    dispatch(Actions.setLoading(false));
};

/*
 * Update one or more of the map note's fields
 * @sig updateMapNote :: () -> async (DispatchFunction, State)
 */
const updateMapNote = venueId => async (dispatch, getState) => {
    const noteId = getId(getState());
    const totals = getTotals(getState());
    const updatedFields = getUpdatedFields(getState());
    const mapNotelets = getMapNotelets(getState());
    dispatch(Actions.setLoading(true));
    try {
        const updatedMapNote = await CanonicalLoader.updateMapNote(venueId, noteId, updatedFields);
        dispatch(Actions.setSelectedMapNote(updatedMapNote));
        updateMapNotelets(noteId, mapNotelets, updatedMapNote)(dispatch);
        if (R.has('noteStatus', updatedFields)) {
            changeMapNoteTally(updatedFields.noteStatus, totals)(dispatch);
        }
        dispatch(Actions.clearUpdatedFields());
    } catch (e) {
        handleException(e);
    }
    dispatch(Actions.setSheetVersion('view'));
    dispatch(Actions.setLoading(false));

    Telemetry.recordSaveMapNote(venueId, noteId);
};

/*
 * MultiThunk
 * Reset the map note fields to default and close the sheet
 * @sig deleteMapNote :: () -> async (DispatchFunction, State)
 */
const deleteMapNote = (venueId, email) => async (dispatch, getState) => {
    const noteId = getId(getState());
    const noteStatus = getStatus(getState());
    const mapNotelets = getMapNotelets(getState());
    const totals = getTotals(getState());
    const deletedMapNotelet = MapNote.MapNote.findMapNotelet(noteId, mapNotelets);
    const updatedMapNotelets = MapNote.MapNote.updateMapNotelet(deletedMapNotelet, mapNotelets);
    const updatedTotals = MapNote.MapNote.deleteNoteFromTotals(totals, noteStatus);
    dispatch(Actions.setLoading(true));
    try {
        await CanonicalLoader.deleteMapNote(venueId, email, noteId);
        dispatch(Actions.setMapNotelets(updatedMapNotelets));
        dispatch(Actions.setTotals(updatedTotals));
    } catch (e) {
        handleException(e);
    }
    dispatch(Actions.setLoading(false));
};

const Thunks = {
    findSelectedMapNote,
    addMapNoteCommentText,
    addMapNoteCommentImages,
    deleteMapNoteComment,
    updateMapNote,
    deleteMapNote,
};

// ---------------------------------------------------------------------------------------------------------------------
//
// HELPER FUNCTIONS
//
// ---------------------------------------------------------------------------------------------------------------------
// TO TEST ----
const getFilterAmount = (totals, filterStatus) => {
    const openAmount = R.propOr(0, 'open', totals);
    const resolvedAmount = R.propOr(0, 'resolved', totals);
    const totalAmount = openAmount + resolvedAmount;
    const amount = filterStatus === 'all' ? totalAmount : R.propOr(0, filterStatus, totals);
    return amount;
};
// Gets an array with the ordinals that contain open map notes, removes undefined from the array
const calculateLevelsWithMapNotes = mapNotelets => R.uniq(R.without([undefined], R.map(getOrdinal, mapNotelets)));
const convertToGeoJson = (mapNotelet, selectedMapNoteId) => ({
    type: 'Feature',
    geometry: {
        type: 'Point',
        coordinates: [Number(mapNotelet.location.longitude), Number(mapNotelet.location.latitude)],
    },
    properties: {
        status:
            R.equals(mapNotelet.id, selectedMapNoteId) || R.equals(mapNotelet.id, -1)
                ? 'mapnote.selected'
                : `mapnote.${mapNotelet.noteStatus}`,
        noteId: mapNotelet.id,
        venueId: mapNotelet.location.venueId,
    },
});

// Pass in the map notelets and the filters that we use to determine whether it shows up on the map or not (status/type/ordinal/map note currently open)
const getFilteredMapNotelets = (mapNotelets, status, type, ordinal, currentId) => {
    const mapNoteFilter = note =>
        (R.includes(note.noteStatus, status) &&
            // Takes all the keys of the type filter that is true, adds null if noType is true,
            // and combines them into a flat array, which we use to see if the note's noteType should be filtered
            R.includes(note.noteType, R.flatten([R.keys(R.filter(val => val, type)), type.noType ? [''] : []])) &&
            `ordinal: ${ordinal ? ordinal.ordinalNumber : parseInt('1', 10)}` === note.location.ordinalId) ||
        note.id < 0 ||
        (!R.isNil(currentId) && note.id === currentId);
    return R.filter(mapNoteFilter, mapNotelets);
};
// ------------
// TO HELP ----
const calculateMapNotesFilterAmount = mapNotes => getFilterAmount(mapNotes.totals, mapNotes.filter.status);
const getOrdinal = mapNote => {
    if (mapNote.noteStatus === 'open') {
        return { ordinalId: mapNote.location.ordinalId, structureId: mapNote.location.structureId };
    }
};
const calculateMapNotesOnLevels = mapNotes => calculateLevelsWithMapNotes(mapNotes.mapNotelets);
const calculateStatus = status => (status === 'all' ? ['open', 'resolved'] : [status]);

// Creates a filter based on status, type, and ordinal and returns the mapNotes that should be shown as GeoJSON
const _calculateGeoJson = (mapNotes, ordinal) => {
    if (!ordinal) return [];
    if (!mapNotes.showNotesOnMap) return [];

    const selectedMapNoteId = R.isNil(mapNotes.selectedMapNote) ? null : mapNotes.selectedMapNote.id;

    const filteredMapNotelets = getFilteredMapNotelets(
        mapNotes.mapNotelets,
        calculateStatus(mapNotes.filter.status),
        mapNotes.filter.type,
        ordinal,
        selectedMapNoteId,
    );

    // now convert to GeoJSON
    return R.map(mapNotelet => convertToGeoJson(mapNotelet, selectedMapNoteId), filteredMapNotelets);
};

const haveMapNotesChanged = (mapNotes, ordinal) => ({
    ordinal,
    showNotesOnMap: mapNotes.showNotesOnMap,
    mapNotelet: mapNotes.mapNotelets,
    selectedMapNote: mapNotes.selectedMapNote,
    filterStatus: mapNotes.filter.status,
    filterType: mapNotes.filter.type,
});

const calculateGeoJson = Functional.memoizeOnceWithObject(haveMapNotesChanged, _calculateGeoJson);

// ------------

const reducer = (state = INITIAL_STATE, action) => {
    switch (action.type) {
        case ActionConstants.SET_TOTALS:
            return handleSetTotals(state, action); // Get back number of open and resolved mapNotes
        case ActionConstants.SET_MAP_NOTELETS:
            return handleSetMapNotelets(state, action); // Array with mapNotelets (objects with enough info to render on the map)
        case ActionConstants.SET_MAP_NOTE_FILTER:
            return handleSetMapNoteFilter(state, action); // Array with all the filters that should be used
        case ActionConstants.SET_SHOW_NOTES_ON_MAP:
            return handleSetShowNotesOnMap(state, action); // Mode for show/no-show mapNotes on map
        case ActionConstants.TOGGLE_NOTES_ON_MAP:
            return handleToggleNotesOnMap(state); // Toggle between showing/not-showing mapNotes
        case ActionConstants.SET_SELECTED_MAP_NOTE:
            return handleSetSelectedMapNote(state, action); // Get back the selected map note
        case ActionConstants.SET_UPDATED_FIELDS:
            return handleSetUpdatedFields(state, action); // Set updates to the map notes field
        case ActionConstants.SET_SHOW_TYPE_DROPDOWN:
            return handleSetShowTypeDropdown(state, action); // Set the dropdown to the open or closed state
        case ActionConstants.SET_SHOW_TRASH_MAP_NOTE_MODAL:
            return handleSetShowTrashMapNoteModal(state, action); // Handle showing the confirmation modal for deleting a map note
        case ActionConstants.SET_SHOW_EXIT_MAP_NOTE_MODAL:
            return handleSetShowExitMapNoteModal(state, action); // Handle showing the confirmation modal for closing a map note sheet
        case ActionConstants.SET_SHOW_CANCEL_CHANGES_MODAL:
            return handleSetShowCancelChangesModal(state, action); // Handle showing the confirmation modal for canceling changes to a map note sheet
        case ActionConstants.CLEAR_UPDATED_FIELDS:
            return handleClearUpdatedFields(state); // Clear fields
        case ActionConstants.SET_COMMENT:
            return handleSetComment(state, action); // Set updates to the map note comment
        case ActionConstants.SET_SHEET_VERSION:
            return handleSetSheetVersion(state, action); // Set the version of the map note sheet to display
        case ActionConstants.SET_LOADING:
            return handleSetLoading(state, action); // Set loading whenever waiting for the server's response
        case ActionConstants.SET_DROPDOWN_SHOWING:
            return handleSetDropdownShowing(state, action); // Set whether the add dropdown menu is visible
        case ActionConstants.SET_FILTER_EXPANDED:
            return handleSetFilterExpanded(state, action); // Set whether the filter menu is visible
        default:
            return state;
    }
};

const Selectors = {
    getTotals,
    getMapNotelets,
    getMapNotesFilter,
    getShowNotesOnMap,
    getMapNotesFilterLabel,
    getMapNotesFilterAmount,
    getConvertedMapNoteletsAsGeoJson,
    findMapNotesOnLevels,
    getSelectedMapNote,
    isTypeDropdownOpen,
    getUpdatedFields,
    getShowExitMapNoteModal,
    getShowTrashMapNoteModal,
    getShowCancelChangesModal,
    getComment,
    getSheetVersion,
    getLoading,
    getDropdownShowing,
    getMapNotelet,
    getFilterExpanded,
};

export { reducer, Actions, Selectors, Thunks };

export default reducer;
